import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import React, { useRef } from 'react';
import './inline-text-input.css';
import { Input } from './input';

// TODO (jimmy): fix how this forward props works
export type InlineTextInputProps = {
  placeholder: string;
  defaultValue?: string;
  className?: string;
  ref?: React.RefObject<HTMLInputElement | null>;
  size?: 'sm';
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
  readOnly?: boolean;
  pattern?: string;
  min?: number;
  max?: number;
  step?: number;
  type?: 'number' | 'text';
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  wrapperClassName?: string;
  autoFocus?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  tabIndex?: number;
};

const InlineTextInput = React.forwardRef<
  HTMLInputElement,
  InlineTextInputProps
>((props, ref) => {
  const {
    placeholder,
    className,
    size = 'sm',
    onBlur,
    onChange,
    value,
    startAdornment,
    endAdornment,
    inputMode = 'text',
    readOnly,
    pattern,
    min,
    max,
    step,
    type,
    onKeyDown,
    wrapperClassName,
    autoFocus,
    onFocus,
    tabIndex,
  } = props;

  const innerRef = useRef<HTMLInputElement>(null);

  return (
    <div
      tabIndex={tabIndex}
      className={cn(
        'inline-text-input relative flex flex-row items-center w-fit',
        wrapperClassName,
      )}
    >
      {startAdornment && (
        <div className={cn('flex items-center pointer-events-none')}>
          {startAdornment}
        </div>
      )}
      <Input
        placeholder={placeholder}
        className={cn(
          'w-full h-fit text-gray-500 py-0 placeholder:text-gray-400 dark:bg-dark dark:text-white',
          { 'text-sm': size == 'sm' },
          className,
        )}
        ref={(el) => {
          if (ref) {
            if (typeof ref === 'function') {
              ref(el);
            } else {
              ref.current = el;
            }
          }

          innerRef.current = el;
        }}
        onBlur={onBlur}
        onChange={onChange}
        value={value}
        inputMode={inputMode}
        readOnly={readOnly}
        pattern={pattern}
        min={min}
        max={max}
        step={step}
        type={type}
        onKeyDown={(event) => {
          if (onKeyDown) {
            onKeyDown(event);
          }

          if (event.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
            if (innerRef.current) {
              innerRef.current.blur();
            }
          }
        }}
        autoFocus={autoFocus}
        onFocus={onFocus}
      />
      {endAdornment && (
        <div className={cn('flex items-center')}>{endAdornment}</div>
      )}
    </div>
  );
});

InlineTextInput.displayName = 'InlineTextInput';
export default InlineTextInput;
